import { FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import { item as TrackingItem } from 'c9-tracking';
import { isPrivateUser } from '../../helpers/topContext';
import { isCustomerClaimCovered } from '../../helpers/business-rules/isCustomerClaimCovered';
import { isCustomerAllowedToSelectWorkshop } from '../../helpers/business-rules/isCustomerAllowedToSelectWorkshop';
import { getClaimDetails, getWorkshopDetails } from '../../conditional-pages/shared/vehicle';

type Scenario =
    | 'serviceUnavailable'
    | 'shopsNotYetSearchedFor'
    | 'customerDoesNotWantShops'
    | 'noShopsFound'
    | 'shopsFoundButNoShopTypeChosen'
    | 'shopsFoundAndShopTypeChosenButNoShopChosen'
    | 'shopsFoundAndShopTypeChosenAndShopChosen';

type WorkshopSelection = 'selected' | 'skipped' | 'unable-to-select';

type SupmanWidgetValue = {
    branch: {
        name: string;
        category: string;
    };
    scenario: Scenario;
    carbucksWorkshops: number;
};

/**
 * You're covered
 * @param formConfig
 * @param value
 */
export const trackYouAreCovered = (formConfig: FormConfig, formValues: FormValues) => {
    const isUserCovered = isCustomerClaimCovered(
        getClaimDetails(formConfig, formValues),
        getWorkshopDetails(formConfig, formValues)
    );

    const items: TrackingItem[] = [];

    // Is user covered
    items.push({ key: 'covered', value: isUserCovered ? 'true' : 'false' });

    return items;
};

export const trackReasons = (formConfig: FormConfig, formValues: FormValues) => {
    const { formId } = formConfig;
    const isCprUser = isPrivateUser();

    const items: TrackingItem[] = [];

    // is user private
    if (!isCprUser) {
        items.push({ key: 'reason-not-a-cpr-user', value: 'true' });
    }

    // Do the customer has access to the workshop selection?
    if (!isCustomerAllowedToSelectWorkshop(getWorkshopDetails(formConfig, formValues), 'cpr')) {
        items.push({ key: 'reason-workshop-selection-will-not-be-shown', value: 'true' });
    }

    // Is driver intoxiacted (not for parkeringsskade-privat)
    if (formId !== 'parkeringsskade-privat') {
        const wasDriverIntoxicated =
            formValues['wasTheDriverIntoxicated'] && formValues['wasTheDriverIntoxicated'].value === 'yes';
        if (wasDriverIntoxicated) {
            items.push({ key: 'reason-was-driver-intoxicated', value: 'true' });
        }
    }

    // Only for ramt-et-koeretoej-privat - situation
    if (formId === 'ramt-et-koeretoej-privat') {
        if (
            formValues['whichStatementMostAccuratelyDescribesTheSituation'] &&
            formValues['whichStatementMostAccuratelyDescribesTheSituation'].value === 'noneOfTheAbove'
        ) {
            items.push({ key: 'reason-situation-no-match', value: 'true' });
        }
    }

    const registrationNumber = formValues['registrationNumber'];

    if (registrationNumber) {
        // Policy number is empty
        if (registrationNumber.policyNrOrOthers && registrationNumber.policyNrOrOthers === '') {
            items.push({ key: 'reason-policy-number-empty', value: 'true' });
        }

        // Do the selected vehichle have kasko in the insurance?
        if (registrationNumber.policyNrOrOthers !== 'others') {
            const hasKasko =
                registrationNumber.coverage &&
                registrationNumber.coverage.find(
                    (coverage: { name: string; status: string }) =>
                        coverage.name.match(/kasko/gi) && coverage.status === 'IKRAFT'
                );
            if (!hasKasko) {
                items.push({ key: 'reason-no-kasko-coverage-found', value: 'true' });
            }
        } else {
            items.push({ key: 'reason-others-selected', value: 'true' });
        }
    }
    return items;
};

/**
 * Supman widget
 * @param formConfig
 * @param value
 */
export const trackSupmanWidget = (formConfig: FormConfig, value: SupmanWidgetValue) => {
    const branch = value.branch;
    const scenario: Scenario = value.scenario;

    switch (scenario) {
        case 'shopsFoundAndShopTypeChosenAndShopChosen':
            return trackWorkshopSelectionByScenario(
                'selected',
                branch.name,
                // In one of the experiments, all workshop type are converted to 'regular'.
                // Hence, we're identifying Carbucks workshop by looking into workshop name.
                value.carbucksWorkshops > 0 || (/carbucks/i.test(branch.name) ?? false)
            );
        case 'customerDoesNotWantShops':
            return trackWorkshopSelectionByScenario('skipped');
        // It should actually not be possible to click Next
        // when the shops have not been searched for
        // However, it yielded tons of roundtrips when I tried defaulting
        // the component to not valid
        // The customer is not forced to choose a shop, so in essence
        // this bug is kind of a feature :-)
        case 'shopsNotYetSearchedFor':
            return trackWorkshopSelectionByScenario('skipped');
        case 'noShopsFound':
            return trackWorkshopSelectionByScenario('unable-to-select');
        case 'serviceUnavailable':
            return trackWorkshopSelectionByScenario('unable-to-select');
        case 'shopsFoundButNoShopTypeChosen':
        case 'shopsFoundAndShopTypeChosenButNoShopChosen':
            return trackWorkshopSelectionByScenario('unable-to-select');
        default:
            return trackWorkshopSelectionByScenario('unable-to-select');
    }
};

const trackWorkshopSelectionByScenario = (
    selection: WorkshopSelection,
    workshopName?: string,
    carbucksOffered?: boolean
) => {
    const trackingItem = {
        key: 'workshop-selection',
        value: selection
    };

    switch (selection) {
        case 'selected':
            return [
                trackingItem,
                { key: 'workshop', value: workshopName ?? '' },
                // In one of the experiments, all workshop type are converted to 'regular'.
                // Therefore, a Carbucks workshop is identified by its name.
                { key: 'workshop-type', value: /carbucks/i.test(workshopName ?? '') ? 'carbucks' : 'regular' },
                {
                    key: 'workshop-carbucks-offered',
                    value: carbucksOffered ?? false
                }
            ];
        case 'skipped':
        case 'unable-to-select':
            return [trackingItem];
    }
};
